import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bold, Italic, Highlighter, List, Link2, Send, Trash2, Loader2,
  MessagesSquare, ImagePlus, SmilePlus, X,
} from "lucide-react";
import {
  crmFetchCRComments, crmAddCRComment, crmDeleteCRComment, crmToggleCRReaction,
  clientFetchCRComments, clientAddCRComment, clientToggleCRReaction, apiErrorMsg,
} from "@/lib/api";
import { compressImageFile } from "@/lib/imageCompress";

const REACTIONS = ["👍", "❤️", "✅"];

/**
 * Two-way comments thread for a Change Request.
 * Used by both the admin CR edit modal and the client CR detail modal.
 *
 * Props:
 *   crId          – change request id
 *   token         – JWT (client) or admin session token (admin)
 *   mode          – "admin" | "client"
 *   currentRole   – "superadmin" | "founders_office" | null  (admin only, controls Delete visibility)
 *   currentUserId – admin_user.id (admin mode) OR client.id (client mode); used to
 *                    highlight the current user's own reactions and to detect "mine".
 *   onCountChange – optional (total) => void  – fires when comments count changes
 */
export default function CRCommentsThread({ crId, token, mode, currentRole = null, currentUserId = "", onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [lightbox, setLightbox] = useState(null);   // { src } — full-screen image preview
  const editorRef = useRef(null);

  const isAdmin = mode === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const data = isAdmin
        ? await crmFetchCRComments(token, crId)
        : await clientFetchCRComments(token, crId);
      const list = Array.isArray(data?.comments) ? data.comments : [];
      setComments(list);
      onCountChange && onCountChange(list.length);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load comments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (crId) load(); /* eslint-disable-next-line */ }, [crId]);

  const post = async () => {
    const html = editorRef.current?.innerHTML || "";
    // require at least some visible text OR an inline image
    const text = html.replace(/<[^>]+>/g, "").trim();
    const hasImage = /<img\b/i.test(html);
    if (!text && !hasImage) { toast.error("Type or paste something before posting"); return; }
    setPosting(true);
    try {
      const c = isAdmin
        ? await crmAddCRComment(token, crId, html)
        : await clientAddCRComment(token, crId, html);
      setComments((prev) => {
        const next = [...prev, c];
        onCountChange && onCountChange(next.length);
        return next;
      });
      if (editorRef.current) editorRef.current.innerHTML = "";
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to post comment"));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await crmDeleteCRComment(token, crId, commentId);
      setComments((prev) => {
        const next = prev.filter((c) => c.id !== commentId);
        onCountChange && onCountChange(next.length);
        return next;
      });
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Delete failed"));
    }
  };

  const toggleReaction = async (commentId, emoji) => {
    // Optimistic-ish: fire request, then reconcile with server's authoritative list.
    try {
      const res = isAdmin
        ? await crmToggleCRReaction(token, crId, commentId, emoji)
        : await clientToggleCRReaction(token, crId, commentId, emoji);
      setComments((prev) => prev.map((c) => (
        c.id === commentId ? { ...c, reactions: res.reactions || [] } : c
      )));
    } catch (e) {
      toast.error(apiErrorMsg(e, "Reaction failed"));
    }
  };

  const canDelete = isAdmin && (currentRole || "superadmin") === "superadmin";
  const currentActorType = isAdmin ? "admin" : "client";

  return (
    <section data-testid={`cr-comments-thread-${crId}`} className="pt-4 border-t border-black/10">
      <header className="flex items-center gap-2 mb-3">
        <MessagesSquare className="h-4 w-4 text-[#ff5722]" />
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#0a0a0a] font-bold">
          Comments <span className="text-[#9a9a9a]">({comments.length})</span>
        </div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a]">
          {isAdmin ? "Reachvel view" : "Client view"}
        </div>
      </header>

      <div className="space-y-3 mb-4 max-h-[380px] overflow-y-auto pr-1">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-[#4a4a4a]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading conversation…
          </div>
        ) : comments.length === 0 ? (
          <div className="text-xs text-[#9a9a9a] italic px-2 py-4 border border-dashed border-black/10 rounded">
            No comments yet. Start the conversation below.
          </div>
        ) : (
          comments.map((c) => (
            <CommentBubble
              key={c.id}
              comment={c}
              currentIsAdmin={isAdmin}
              currentActorType={currentActorType}
              currentUserId={currentUserId}
              canDelete={canDelete}
              onDelete={() => remove(c.id)}
              onToggleReaction={(emoji) => toggleReaction(c.id, emoji)}
              onImageClick={(src) => setLightbox({ src })}
            />
          ))
        )}
      </div>

      {/* Composer */}
      <Composer
        editorRef={editorRef}
        onPost={post}
        posting={posting}
        placeholder={isAdmin ? "Write a reply to the client…" : "Ask a question or reply to Reachvel…"}
        testidPrefix={isAdmin ? "cr-comments-admin" : "cr-comments-client"}
      />

      {/* Full-screen image lightbox — click anywhere / Esc to close */}
      {lightbox && (
        <ImageLightbox src={lightbox.src} onClose={() => setLightbox(null)} />
      )}
    </section>
  );
}

// Display-friendly name: hide internal admin usernames behind "Reachvel"
// so the client (and the admin themselves) always see uniform Reachvel branding.
function displayName(actorType, rawName) {
  if (actorType === "admin") return "Reachvel";
  return rawName || "Client";
}

// ─────────────────────── Comment bubble ───────────────────────
function CommentBubble({ comment, currentIsAdmin, currentActorType, currentUserId,
                          canDelete, onDelete, onToggleReaction, onImageClick }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const bubbleRef = useRef(null);
  // Close picker on outside click / Esc
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setPickerOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);
  // Colour tone: Reachvel bubble = orange-tinted; Client bubble = neutral.
  const isReachvel = comment.author_type === "admin";
  const isMine = (currentIsAdmin && isReachvel) || (!currentIsAdmin && !isReachvel);
  const authorLabel = displayName(comment.author_type, comment.author_name);
  const initial = isReachvel ? "R" : (comment.author_name || "?").trim().charAt(0).toUpperCase();
  const ts = useMemo(() => fmtStamp(comment.created_at), [comment.created_at]);

  // Group reactions by emoji → { emoji, count, actors[], mine }
  // Actor names are collapsed via displayName so clients never see internal admin usernames.
  const grouped = useMemo(() => {
    const list = comment.reactions || [];
    const map = new Map();
    for (const r of list) {
      const key = r.emoji;
      const bucket = map.get(key) || { emoji: key, count: 0, actors: [], mine: false };
      bucket.count += 1;
      bucket.actors.push(displayName(r.actor_type, r.actor_name));
      if (r.actor_type === currentActorType && r.actor_id === currentUserId) bucket.mine = true;
      map.set(key, bucket);
    }
    // Stable order: 👍 ❤️ ✅
    return REACTIONS.map((e) => map.get(e)).filter(Boolean);
  }, [comment.reactions, currentActorType, currentUserId]);

  const handlePick = (emoji) => {
    setPickerOpen(false);
    onToggleReaction && onToggleReaction(emoji);
  };

  // Delegate clicks on any <img> inside the rendered comment body → open lightbox
  const onBubbleClick = (e) => {
    const t = e.target;
    if (t && t.tagName === "IMG" && t.src) {
      onImageClick && onImageClick(t.src);
    }
  };

  return (
    <div
      data-testid={`cr-comment-${comment.id}`}
      className={`group flex gap-2.5 ${isMine ? "flex-row-reverse" : ""}`}
    >
      <div
        className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold ${
          isReachvel
            ? "bg-[#ff5722] text-white"
            : "bg-[#0a0a0a] text-white"
        }`}
        title={authorLabel}
      >
        {initial}
      </div>
      <div className={`min-w-0 max-w-[85%] ${isMine ? "text-right" : ""}`}>
        <div className={`flex items-center gap-2 mb-1 text-[10px] font-mono uppercase tracking-[0.12em] ${isMine ? "justify-end" : ""}`}>
          <span className={`font-bold ${isReachvel ? "text-[#ff5722]" : "text-[#0a0a0a]"}`}>
            {authorLabel}
          </span>
          <span className="text-[#9a9a9a]">·</span>
          <span className="text-[#9a9a9a]" title={comment.created_at}>{ts}</span>
          {canDelete && (
            <button
              onClick={onDelete}
              data-testid={`cr-comment-delete-${comment.id}`}
              className="ml-1 p-0.5 rounded hover:bg-rose-50 text-rose-600"
              title="Delete comment"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className={`relative ${isMine ? "inline-block" : ""}`}>
          <div
            ref={bubbleRef}
            onClick={onBubbleClick}
            className={`inline-block text-left rounded-lg px-3 py-2 text-sm break-words comment-bubble border ${
              isReachvel
                ? "bg-[#fff2ec] border-[#ff5722]/30 text-[#0a0a0a]"
                : "bg-[#f7f6f3] border-black/10 text-[#0a0a0a]"
            }`}
            dangerouslySetInnerHTML={{ __html: comment.body_html || "" }}
          />
        </div>

        {/* Reactions row — pills for existing reactions + tiny "+ smile" trigger. */}
        <div className={`mt-1.5 flex items-center gap-1.5 flex-wrap ${isMine ? "justify-end" : ""}`}>
          {grouped.map((r) => (
            <button
              key={r.emoji}
              type="button"
              onClick={() => onToggleReaction && onToggleReaction(r.emoji)}
              title={r.actors.join(", ")}
              data-testid={`cr-comment-${comment.id}-reaction-${r.emoji}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] leading-none border transition-colors ${
                r.mine
                  ? "bg-[#ff5722]/10 border-[#ff5722] text-[#ff5722] font-semibold"
                  : "bg-white border-black/15 text-[#0a0a0a] hover:border-[#ff5722]/40"
              }`}
            >
              <span>{r.emoji}</span>
              <span className="font-mono">{r.count}</span>
            </button>
          ))}
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid={`cr-comment-${comment.id}-react-add`}
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border border-black/15 text-[#9a9a9a] hover:text-[#ff5722] hover:border-[#ff5722]/40 bg-white transition-opacity ${
                grouped.length === 0 ? "opacity-0 group-hover:opacity-100 focus:opacity-100" : "opacity-100"
              }`}
              title="React"
            >
              <SmilePlus className="h-3.5 w-3.5" />
            </button>
            {pickerOpen && (
              <div
                className={`absolute z-10 mt-1 bg-white border border-black/10 rounded-full shadow-md px-1.5 py-1 flex items-center gap-0.5 ${isMine ? "right-0" : "left-0"}`}
                data-testid={`cr-comment-${comment.id}-react-palette`}
              >
                {REACTIONS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => handlePick(e)}
                    data-testid={`cr-comment-${comment.id}-react-pick-${e}`}
                    className="p-1 rounded-full hover:bg-black/5 text-base leading-none"
                    title={`React with ${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────── Image Lightbox ───────────────────────
// Full-screen viewer for any inline <img> tapped inside a comment bubble.
// Closes on Esc, click on backdrop, or the ✕ button.
function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Prevent background scroll while lightbox is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-testid="cr-comment-image-lightbox"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 md:p-8 cursor-zoom-out"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="cr-comment-image-lightbox-close"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        title="Close (Esc)"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt="Attachment preview"
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default"
      />
    </div>
  );
}

// ─────────────────────── Composer ───────────────────────
function Composer({ editorRef, onPost, posting, placeholder, testidPrefix }) {
  const fileInputRef = useRef(null);

  const exec = (cmd, val) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val || undefined);
  };
  const highlight = () => {
    // Wrap selected text in <mark>. Falls back to hiliteColor if selection empty.
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      const range = sel.getRangeAt(0);
      const mark = document.createElement("mark");
      mark.style.background = "#ffeb3b";
      mark.style.padding = "0 2px";
      try {
        range.surroundContents(mark);
      } catch {
        // If selection crosses tags, fallback to hiliteColor
        exec("hiliteColor", "#ffeb3b");
      }
      sel.removeAllRanges();
    } else {
      exec("hiliteColor", "#ffeb3b");
    }
  };
  const insertLink = () => {
    const url = window.prompt("Link URL:");
    if (!url) return;
    if (/^\s*javascript:/i.test(url)) { toast.error("Unsafe link blocked"); return; }
    exec("createLink", url);
  };

  // Compress an image file → insert as inline <img> (base64 JPEG, < ~900 KB).
  const insertCompressedImage = async (file) => {
    try {
      const { dataUrl } = await compressImageFile(file);
      editorRef.current?.focus();
      document.execCommand(
        "insertHTML",
        false,
        `<img src="${dataUrl}" style="max-width:100%;height:auto;border-radius:6px;margin:6px 0;display:block;" />`,
      );
    } catch {
      toast.error("Could not attach that image");
    }
  };

  const onKeyDown = (e) => {
    // Ctrl/Cmd+Enter posts, plain Enter creates a newline (default behaviour).
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      onPost();
    }
  };

  const onPaste = (e) => {
    // 1. Handle pasted images from clipboard — compress before embedding
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) insertCompressedImage(file);
        return;
      }
    }
    // 2. Otherwise plain text paste (strip formatting so threads stay clean)
    if (e.clipboardData) {
      const text = e.clipboardData.getData("text/plain");
      if (text) {
        e.preventDefault();
        document.execCommand("insertText", false, text);
      }
    }
  };

  const onDrop = (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    e.preventDefault();
    images.forEach((f) => insertCompressedImage(f));
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f && f.type.startsWith("image/")) insertCompressedImage(f);
  };

  return (
    <div className="border border-black/15 rounded-lg bg-white overflow-hidden" data-testid={`${testidPrefix}-composer`}>
      <div className="flex items-center gap-0.5 border-b border-black/10 bg-[#f7f6f3] px-2 py-1.5">
        <TBtn onClick={() => exec("bold")}   title="Bold"        testid={`${testidPrefix}-bold`}><Bold className="h-3.5 w-3.5" /></TBtn>
        <TBtn onClick={() => exec("italic")} title="Italic"      testid={`${testidPrefix}-italic`}><Italic className="h-3.5 w-3.5" /></TBtn>
        <TBtn onClick={highlight}            title="Highlight"   testid={`${testidPrefix}-highlight`}><Highlighter className="h-3.5 w-3.5" /></TBtn>
        <div className="w-px h-4 bg-black/15 mx-1" />
        <TBtn onClick={() => exec("insertUnorderedList")} title="Bullet list" testid={`${testidPrefix}-ul`}><List className="h-3.5 w-3.5" /></TBtn>
        <TBtn onClick={insertLink}           title="Insert link" testid={`${testidPrefix}-link`}><Link2 className="h-3.5 w-3.5" /></TBtn>
        <TBtn onClick={() => fileInputRef.current?.click()} title="Insert image" testid={`${testidPrefix}-image`}>
          <ImagePlus className="h-3.5 w-3.5" />
        </TBtn>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFilePick} data-testid={`${testidPrefix}-image-input`} />
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a] pr-1">
          Paste screenshots · Ctrl+Enter to post
        </span>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-testid={`${testidPrefix}-editor`}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="composer-body px-3 py-2 text-sm outline-none"
        style={{ minHeight: 72, maxHeight: 340, overflowY: "auto" }}
      />
      <div className="flex items-center justify-end gap-2 border-t border-black/10 px-2 py-1.5 bg-[#fafafa]">
        <button
          type="button"
          onClick={onPost}
          disabled={posting}
          data-testid={`${testidPrefix}-post`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] disabled:opacity-50 transition-colors"
        >
          {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {posting ? "Posting…" : "Post comment"}
        </button>
      </div>

      <style>{`
        .composer-body:empty::before {
          content: attr(data-placeholder);
          color: #9a9a9a;
          pointer-events: none;
        }
        .composer-body a { color: #ff5722; text-decoration: underline; }
        .composer-body ul, .composer-body ol { padding-left: 22px; margin: 4px 0; }
        .composer-body mark { background: #ffeb3b; padding: 0 2px; border-radius: 2px; }
        .composer-body img { max-width: 100%; height: auto; border-radius: 6px; margin: 6px 0; display: block; }
        .comment-bubble a { color: #ff5722; text-decoration: underline; }
        .comment-bubble ul, .comment-bubble ol { padding-left: 22px; margin: 4px 0; }
        .comment-bubble mark { background: #ffeb3b; padding: 0 2px; border-radius: 2px; }
        .comment-bubble img { max-width: 100%; height: auto; border-radius: 6px; margin: 6px 0; display: block; cursor: zoom-in; }
        .comment-bubble p { margin: 4px 0; }
      `}</style>
    </div>
  );
}

function TBtn({ children, onClick, title, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-1.5 rounded hover:bg-black/5 text-[#4a4a4a] hover:text-[#0a0a0a]"
    >
      {children}
    </button>
  );
}

// Formats "16 Sep 2026 · 07:45" — locale-friendly with exact time.
function fmtStamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} · ${time}`;
  } catch { return iso; }
}
